import type {
  Deviation_Survey_Insert_Input,
  Revisions_Scenario_Equipment_Xref_Pumps_Revisions_Insert_Input,
  SizingsManifoldMutationVariables
} from "gql/api";
import type { ExportedScenario } from "@/utils/exports/exportScenario";
import { jsonToPostgresArray } from "@/utils/utils-dependent";
import { useSizingsManifoldMutation } from "gql/api";
import { haltUntilCompletion } from "./exchanges/mutationLimiter";
import useCreateRevisionFromQuery from "./useCreateRevisionFromQuery";
import useDestructureScenario from "./useDestructureScenario";
import transplantJournal from "@/utils/transplantJournal";
import { useMutationNotification } from "./logger/mutationNotification";

export default function (): (
  exportedScenario: ExportedScenario
) => Promise<ExportedScenario["payload"] | undefined> {
  const { executeMutation } = useSizingsManifoldMutation();
  const { registerResultToast } = useMutationNotification();

  return async function (exportedScenario: ExportedScenario) {
    const transplantedScenario = transplantJournal(exportedScenario.payload);

    const {
      scenario,
      correlations,
      equipment,
      parameters,
      wellProperties,
      deviationSurvey,
      intakes,
      pumps,
      housings,
      cables,
      seals
    } = useDestructureScenario(
      transplantedScenario as ExportedScenario["payload"]
    );

    const sizings_revisions: Record<string, unknown>[] = [];

    const scenarios_revisions = [
      useCreateRevisionFromQuery(scenario.value, [
        "author",
        "correlations",
        "equipment",
        "parameters",
        "well_properties"
      ])
    ];
    const scenario_correlations_revisions = [
      useCreateRevisionFromQuery(correlations.value)
    ];
    const deviation_survey = [
      {
        ...useCreateRevisionFromQuery(deviationSurvey.value),
        entry_rows: jsonToPostgresArray(
          deviationSurvey.value?.entry_rows || null
        )
      }
    ] as Deviation_Survey_Insert_Input[];

    const scenario_equipment_revisions = [
      {
        ...useCreateRevisionFromQuery(equipment.value),
        seals_order: jsonToPostgresArray(equipment.value?.seals_order || null),
        cables_order: jsonToPostgresArray(
          equipment.value?.cables_order || null
        ),
        pumps_order: jsonToPostgresArray(equipment.value?.pumps_order || null),
        intakes_order: jsonToPostgresArray(
          equipment.value?.intakes_order || null
        )
      }
    ] as unknown as Deviation_Survey_Insert_Input[];

    const scenario_parameters_revisions = [
      useCreateRevisionFromQuery(parameters.value)
    ];
    const scenario_well_properties_revisions = [
      useCreateRevisionFromQuery(wellProperties.value)
    ];

    const scenario_equipment_xref_cables_revisions = Object.values(
      cables.value
    ).map(item => useCreateRevisionFromQuery(item));

    const scenario_equipment_xref_housings_revisions = Object.values(
      housings.value
    ).map(item => useCreateRevisionFromQuery(item));

    const scenario_equipment_xref_intakes_revisions = Object.values(
      intakes.value
    ).map(item => useCreateRevisionFromQuery(item));

    const scenario_equipment_xref_pumps_revisions = Object.values(pumps.value)
      .map(item => useCreateRevisionFromQuery(item))
      .map(
        (
          pump: Revisions_Scenario_Equipment_Xref_Pumps_Revisions_Insert_Input
        ) => {
          return {
            ...pump,
            housings_order: jsonToPostgresArray(pump.housings_order || null)
          };
        }
      ) as unknown as Deviation_Survey_Insert_Input[];

    const scenario_equipment_xref_seals_revisions = Object.values(
      seals.value
    ).map(item => useCreateRevisionFromQuery(item));

    const scenario_equipment_xref_uphole_y_tool_bypass_tubings_revisions = (
      equipment.value?.r_xref_uphole_y_tool_bypass_tubings || []
    ).map(item => useCreateRevisionFromQuery(item));

    const scenario_equipment_xref_uphole_y_tool_equipment_tubings_revisions = (
      equipment.value?.r_xref_uphole_y_tool_equipment_tubings || []
    ).map(item => useCreateRevisionFromQuery(item));

    const scenario_equipment_xref_downhole_y_tool_bypass_tubings_revisions = (
      equipment.value?.r_xref_downhole_y_tool_bypass_tubings || []
    ).map(item => useCreateRevisionFromQuery(item));

    const scenario_equipment_xref_downhole_y_tool_equipment_tubings_revisions = (
      equipment.value?.r_xref_downhole_y_tool_equipment_tubings || []
    ).map(item => useCreateRevisionFromQuery(item));

    const variables: SizingsManifoldMutationVariables = {
      sizings_revisions,
      scenarios_revisions,
      scenario_correlations_revisions,
      deviation_survey,
      scenario_equipment_revisions,
      scenario_parameters_revisions,
      scenario_well_properties_revisions,
      scenario_equipment_xref_cables_revisions,
      scenario_equipment_xref_housings_revisions,
      scenario_equipment_xref_intakes_revisions,
      scenario_equipment_xref_pumps_revisions,
      scenario_equipment_xref_seals_revisions,
      sc_equip_xref_uphole_ytool_bypass_tubings_revisions:
        scenario_equipment_xref_uphole_y_tool_bypass_tubings_revisions,
      sc_equip_xref_uphole_ytool_equip_tubings_revisions:
        scenario_equipment_xref_uphole_y_tool_equipment_tubings_revisions,
      sc_equip_xref_downhole_ytool_bypass_tubings_revisions:
        scenario_equipment_xref_downhole_y_tool_bypass_tubings_revisions,
      sc_equip_xref_downhole_ytool_equip_tubings_revisions:
        scenario_equipment_xref_downhole_y_tool_equipment_tubings_revisions
    };

    const result = executeMutation(variables);
    registerResultToast("Scenario Clone", result);

    await haltUntilCompletion();
    return transplantedScenario as ExportedScenario["payload"];
  };
}
